import { useEffect, useState } from 'react'
import { catalogo } from '../api/servicios'
import { dinero } from '../api/formato'
import type { Producto } from '../api/tipos'
import { usarRetardo } from './Paginador'
import { EscanerCamara, hayCamaraPosible } from './EscanerCamara'
import { mensajeDeError } from './usarCargar'

interface Props {
    etiqueta?: string
    alElegir: (producto: Producto) => void
}

/**
 * Busca un producto por nombre, SKU o codigo de barras y lo devuelve elegido.
 *
 * Acepta el lector de codigo de barras sin tratarlo distinto: el lector escribe
 * el codigo en el campo, la busqueda lo resuelve y el producto queda arriba de
 * la lista.
 */
export function BuscadorProducto({ etiqueta = 'Producto', alElegir }: Props) {
    const [texto, setTexto] = useState('')
    const [resultados, setResultados] = useState<Producto[]>([])
    const [error, setError] = useState<string | null>(null)
    const [buscando, setBuscando] = useState(false)
    const [camara, setCamara] = useState(false)

    const busqueda = usarRetardo(texto)

    useEffect(() => {
        if (busqueda.trim().length < 2) {
            setResultados([])
            return
        }

        const control = new AbortController()

        setBuscando(true)
        setError(null)

        catalogo
            .buscarProductos(busqueda.trim(), control.signal)
            .then((pagina) => setResultados(pagina.items))
            .catch((fallo) => {
                // Abortar una busqueda que ya no interesa no es un error que
                // el usuario tenga que ver.
                if (control.signal.aborted) return
                setError(mensajeDeError(fallo))
            })
            .finally(() => {
                if (!control.signal.aborted) setBuscando(false)
            })

        return () => control.abort()
    }, [busqueda])

    return (
        <div>
            <label className="etiqueta" htmlFor="buscador-producto">
                {etiqueta}
            </label>

            <div className="fila g2">
                <input
                    id="buscador-producto"
                    className="campo crece"
                    type="search"
                    autoComplete="off"
                    placeholder="Nombre, SKU o código de barras"
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                />

                {/* La camara escribe en este mismo campo: de ahi para abajo es
                    identico a teclear el codigo o pasarlo con la pistola. */}
                {hayCamaraPosible() && (
                    <button
                        type="button"
                        className="boton"
                        onClick={() => setCamara(true)}
                        title="Escanear con la cámara"
                    >
                        Cámara
                    </button>
                )}
            </div>

            {camara && (
                <EscanerCamara
                    modo="unico"
                    alLeer={(codigo) => setTexto(codigo)}
                    alCerrar={() => setCamara(false)}
                />
            )}

            {error && (
                <div className="aviso aviso-error" role="alert" style={{ marginTop: 'var(--e2)' }}>
                    {error}
                </div>
            )}

            {buscando && resultados.length === 0 && (
                <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                    Buscando…
                </p>
            )}

            {resultados.length > 0 && (
                <ul className="sugerencias">
                    {resultados.map((p) => (
                        <li key={p.productoId}>
                            <button
                                type="button"
                                onClick={() => {
                                    alElegir(p)
                                    setTexto('')
                                    setResultados([])
                                }}
                            >
                                <span className="fila entre g3">
                                    <span className="truncar">{p.nombre}</span>
                                    <span className="numero chico tenue">
                                        {dinero(p.precioVigente)}
                                    </span>
                                </span>
                                <span className="tenue diminuto numero">{p.sku}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
